import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  CustomUserField,
  toCustomFieldKey
} from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { uniq } from 'lodash';
import {
  fromMoment,
  timestampToMillisSafe,
  toMomentFromAny
} from '../../../../services/time';
// Reuse the app's canonical field-type resolver (standard + custom). `import
// type` keeps this a pure module — no MUI/React pulled in from EditFieldDialog.
import type { FieldType } from '../EditFieldDialog';
import { getFieldType } from '../EditFieldDialog/utils';

/** Which record a clash is currently resolved to. */
export type MergeSide = 'profile' | 'other';

export interface ClashField {
  /** Dotted Firestore path — e.g. `email` or `customFields.greenhouseId`. */
  key: string;
  /**
   * Value-type, resolved via `getFieldType` — used to compare the two records
   * and serialize the chosen value for the backend.
   */
  type: FieldType;
  /** Raw stored value on the initiating (profile) record. */
  valueProfile: unknown;
  /** Raw stored value on the other record. */
  valueOther: unknown;
  /**
   * The side we pre-select. Defaults to the surviving record's value; when that
   * side is empty and the other has a value, defaults to the non-empty one so
   * nothing is silently lost.
   */
  defaultSide: MergeSide;
}

/**
 * Identity/audit fields the backend refuses to overwrite (and that would be
 * meaningless to "resolve" by hand). Every other persisted field is fair game,
 * so we diff them all rather than maintaining an allowlist. The `updatedBy*`
 * variants are listed defensively so whichever name the backend uses is
 * covered.
 */
const NON_MERGEABLE_KEYS = new Set<string>([
  'id',
  'orgId',
  'createdAt',
  'createdByUserId',
  'updatedAt',
  'updatedBy',
  'updatedByUserId'
]);

// `customFields` IS mergeable, but per-key (`customFields.<key>`) — the custom
// fields loop below handles it, so it's skipped in the top-level pass.
const CUSTOM_FIELDS_KEY = 'customFields';

/**
 * Preferred display order for the common standard fields (mirrors the Profile
 * Data tab). Any other persisted field that clashes is appended after these,
 * sorted by label.
 */
const STANDARD_FIELD_ORDER: string[] = [
  'email',
  'personalEmail',
  'role',
  'department',
  'location',
  'managerId',
  'preferredName',
  'firstName',
  'middleName',
  'lastName',
  'phoneNumber',
  'alternateId',
  'team',
  'hireDate',
  'terminationDate',
  'dateOfBirth',
  'address',
  'gender'
];

const normalizeAddress = (a: any): string =>
  [
    a?.name,
    a?.addressLine1,
    a?.addressLine2,
    a?.city,
    a?.region,
    a?.postalCode,
    a?.country
  ]
    .map((x) => (x || '').toString().trim())
    .join(' ');

/**
 * Whether a raw stored value counts as "no value" for its type. `false` and
 * `0` are real values; only null/undefined/'' (and empty dates/addresses) are
 * empty.
 */
export const isEmptyValue = (type: FieldType, v: unknown): boolean => {
  if (v === null || v === undefined || v === '') {
    return true;
  }
  switch (type) {
    case 'date':
      return timestampToMillisSafe(v) === undefined;
    case 'address':
      return normalizeAddress(v).replace(/ /g, '') === '';
    default:
      return false;
  }
};

const valuesEqual = (type: FieldType, a: unknown, b: unknown): boolean => {
  const aEmpty = isEmptyValue(type, a);
  const bEmpty = isEmptyValue(type, b);
  if (aEmpty && bEmpty) {
    return true;
  }
  // Empty-vs-non-empty is a clash — that's how a value present on only one
  // record gets carried across.
  if (aEmpty !== bEmpty) {
    return false;
  }
  switch (type) {
    case 'date':
      return timestampToMillisSafe(a) === timestampToMillisSafe(b);
    case 'address':
      return normalizeAddress(a) === normalizeAddress(b);
    case 'number':
      return Number(a) === Number(b);
    default:
      return String(a) === String(b);
  }
};

const computeDefaultSide = (
  type: FieldType,
  valueProfile: unknown,
  valueOther: unknown,
  keptSide: MergeSide
): MergeSide => {
  const deletedSide: MergeSide = keptSide === 'profile' ? 'other' : 'profile';
  const keptValue = keptSide === 'profile' ? valueProfile : valueOther;
  const deletedValue = keptSide === 'profile' ? valueOther : valueProfile;
  // Prefer the surviving record's value; fall back to the other record's value
  // when the survivor's is empty (so a one-sided value is never dropped).
  if (isEmptyValue(type, keptValue) && !isEmptyValue(type, deletedValue)) {
    return deletedSide;
  }
  return keptSide;
};

/**
 * Serialize a chosen value into the SAME shape `user_updateField` sends, so the
 * backend's Timestamp normalizer sees exactly what it expects.
 */
export const serializeClashValue = (type: FieldType, v: unknown): unknown => {
  if (isEmptyValue(type, v)) {
    switch (type) {
      case 'date':
      case 'address':
      case 'number':
        return null;
      default:
        // string / email / relationship
        return '';
    }
  }
  switch (type) {
    case 'date':
      // Normalize any stored shape (Timestamp | {seconds,nanoseconds} |
      // {_seconds,_nanoseconds}) back into a real Timestamp, which serializes
      // to { seconds, nanoseconds } over the wire.
      return fromMoment(toMomentFromAny(v as any));
    case 'number':
      return Number(v);
    default:
      // string / email / relationship → raw value; address → object
      return v;
  }
};

const getStandardValue = (user: Doc<IUser>, key: string): unknown =>
  (user.data as any)[key];

const getCustomValue = (user: Doc<IUser>, key: string): unknown =>
  (user.data.customFields || {})[key];

/**
 * Every field whose value differs between the two records — standard fields
 * plus the union of both records' custom-field keys (including external system
 * IDs). Smart (computed) custom fields are skipped: the backend derives them.
 * One-sided differences (a value on only one record) are included, defaulting
 * to the set value, so it carries onto the survivor; the UI hides those and
 * only shows true conflicts (see `isDisplayableClash`).
 */
export const computeClashes = (
  profileUser: Doc<IUser>,
  otherUser: Doc<IUser>,
  customUserFields: CustomUserField[],
  // Which record survives the merge. Defaults are computed to favor this side.
  // Defaults to 'profile' (the initiating record) when unknown.
  keptSide: MergeSide = 'profile'
): ClashField[] => {
  const clashes: ClashField[] = [];

  const pushClash = (
    key: string,
    valueProfile: unknown,
    valueOther: unknown
  ) => {
    const type = getFieldType(key, customUserFields);
    if (!valuesEqual(type, valueProfile, valueOther)) {
      clashes.push({
        key,
        type,
        valueProfile,
        valueOther,
        defaultSide: computeDefaultSide(
          type,
          valueProfile,
          valueOther,
          keptSide
        )
      });
    }
  };

  // Standard (top-level) fields — every persisted key on either record except
  // the non-mergeable identity/audit fields. Ordered by STANDARD_FIELD_ORDER,
  // with any other persisted keys appended alphabetically.
  const standardKeys = uniq([
    ...Object.keys(profileUser.data),
    ...Object.keys(otherUser.data)
  ]).filter((k) => !NON_MERGEABLE_KEYS.has(k) && k !== CUSTOM_FIELDS_KEY);
  const orderedStandardKeys = [
    ...STANDARD_FIELD_ORDER.filter((k) => standardKeys.includes(k)),
    ...standardKeys.filter((k) => !STANDARD_FIELD_ORDER.includes(k)).sort()
  ];
  orderedStandardKeys.forEach((key) => {
    pushClash(
      key,
      getStandardValue(profileUser, key),
      getStandardValue(otherUser, key)
    );
  });

  // Custom fields — union of org definitions and any keys present on either
  // record. Smart (computed) fields are excluded (the backend derives them).
  const smartKeys = new Set(
    customUserFields
      .filter((f) => f.type === 'smartDate' || f.type === 'smartText')
      .map((f) => f.key)
  );
  const customKeys = uniq([
    ...customUserFields.map((f) => f.key),
    ...Object.keys(profileUser.data.customFields || {}),
    ...Object.keys(otherUser.data.customFields || {})
  ])
    .filter((k) => !smartKeys.has(k))
    .sort();

  customKeys.forEach((k) => {
    pushClash(
      toCustomFieldKey(k),
      getCustomValue(profileUser, k),
      getCustomValue(otherUser, k)
    );
  });

  return clashes;
};

/**
 * A clash needs an admin decision only when BOTH records hold a value. When
 * exactly one side has a value there's nothing to choose — it's hidden and the
 * set value is applied automatically (via its `defaultSide`).
 */
export const isDisplayableClash = (c: ClashField): boolean =>
  !isEmptyValue(c.type, c.valueProfile) && !isEmptyValue(c.type, c.valueOther);

/**
 * Build the winner-agnostic `resolvedFields` map: for each clash, the chosen
 * side's value serialized into the backend's expected shape.
 */
export const buildResolvedFields = (
  clashes: ClashField[],
  selections: Record<string, MergeSide>
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  clashes.forEach((c) => {
    const side = selections[c.key] || c.defaultSide;
    const raw = side === 'profile' ? c.valueProfile : c.valueOther;
    out[c.key] = serializeClashValue(c.type, raw);
  });
  return out;
};
